import { useEffect, useState } from "react";
import http from "@/api";
import { Activity, Smartphone, Monitor, Eye, Share2, LogIn, GraduationCap, Clock, BarChart3, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const PILLAR_LABELS = {
  calidad: "Calidad", costo: "Costo", continuidad: "Nivel de Servicio",
  innovacion: "Innovación", sustentabilidad: "Sostenibilidad", relacion: "Cumplimiento",
};

export default function DemoAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/demo-analytics", { params: { days: d } });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="px-6 py-20 text-center font-hand text-2xl text-[var(--lala-blue)]">Cargando analytics…</div>;

  const funnelData = [
    { label: "Vista", value: data.funnel.page_view, color: "#0033AD" },
    { label: "Scroll 50%", value: data.funnel.scroll_50, color: "#4F69D6" },
    { label: "Scroll 100%", value: data.funnel.scroll_100, color: "#7C8EE5" },
    { label: "Compartir", value: data.funnel.click_share, color: "#10B981" },
    { label: "Aprendizaje", value: data.funnel.click_learning, color: "#F59E0B" },
    { label: "Acceder a Lala", value: data.funnel.click_login_cta, color: "#E0162B" },
  ];
  const pillarData = Object.entries(data.pillars_viewed || {})
    .map(([k, v]) => ({ pillar: PILLAR_LABELS[k] || k, views: v })).sort((a, b) => b.views - a.views);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="demo-analytics-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">analytics del demo público</div>
          <h1 className="font-display text-4xl">¿Quién entró y qué hizo en /demo?</h1>
          <p className="text-sm text-[var(--ink-soft)] mt-1 max-w-2xl">
            Tracking session-based sin PII. Eventos: vistas, scroll-depth, clics en CTAs, conversiones a /login y permanencia.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => { setDays(d); load(d); }}
              data-testid={`range-${d}d-btn`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition ${
                days === d ? "bg-[var(--lala-blue)] text-white" : "bg-white border border-[var(--line)] text-[var(--ink)]"
              }`}>últimos {d} días</button>
          ))}
          <button onClick={() => load()} disabled={loading} className="btn-ghost text-sm" data-testid="refresh-btn">
            <RefreshCw size={14} /> {loading ? "Cargando…" : "Recargar"}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi icon={<Eye size={18} />} label="Sesiones únicas" value={data.total_sessions} testid="kpi-sessions" />
        <Kpi icon={<Activity size={18} />} label="Eventos totales" value={data.total_events} testid="kpi-events" />
        <Kpi icon={<Smartphone size={18} />} label="% Mobile"
          value={`${data.mobile_pct}%`} sub={`${data.mobile_sessions} mobile · ${data.desktop_sessions} desktop`} testid="kpi-mobile" />
        <Kpi icon={<Clock size={18} />} label="Tiempo promedio"
          value={`${data.avg_duration_s}s`} sub={`mediana ${data.median_duration_s}s`} testid="kpi-time" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi icon={<LogIn size={18} />} label="Conversiones a /login"
          value={data.funnel.click_login_cta} sub={`${data.conversion_rate_pct}% del total`} testid="kpi-conv" highlight />
        <Kpi icon={<Share2 size={18} />} label="Compartieron el link" value={data.funnel.click_share} testid="kpi-share" />
        <Kpi icon={<GraduationCap size={18} />} label="Vieron Aprendizaje" value={data.funnel.click_learning} testid="kpi-learning" />
        <Kpi icon={<BarChart3 size={18} />} label="Llegaron al 100%" value={data.scroll_depth_sessions[100]}
          sub={`de ${data.total_sessions} sesiones`} testid="kpi-scroll100" />
      </div>

      {/* Funnel chart */}
      <div className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="funnel-card">
        <div className="font-hand text-xl text-[var(--lala-blue)]">embudo de la experiencia</div>
        <h3 className="font-display text-2xl mb-3">Del primer scroll a la conversión</h3>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer width="100%" height={280} minWidth={200} minHeight={160}>
            <BarChart data={funnelData} layout="vertical" margin={{ left: 30, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#D9CFB9" />
              <XAxis type="number" stroke="#11142B" />
              <YAxis type="category" dataKey="label" stroke="#11142B" width={130} />
              <Tooltip contentStyle={{ background: "#FBF7EE", border: "1.5px solid #0033AD", borderRadius: 12 }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {funnelData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Scroll depth & pillar interest */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="scroll-card">
          <div className="font-hand text-xl text-[var(--lala-blue)]">profundidad de scroll</div>
          <h3 className="font-display text-2xl mb-3">Cuánto leyeron de la página</h3>
          <div className="space-y-3">
            {[25, 50, 75, 100].map((pct) => {
              const sessions = data.scroll_depth_sessions[pct] || 0;
              const ratio = data.total_sessions ? (sessions / data.total_sessions) * 100 : 0;
              return (
                <div key={pct} data-testid={`scroll-bucket-${pct}`}>
                  <div className="flex justify-between text-sm font-semibold mb-1">
                    <span>llegaron al {pct}%</span>
                    <span className="text-[var(--lala-blue)] font-display">{sessions} <span className="text-xs text-[var(--ink-soft)]">({ratio.toFixed(0)}%)</span></span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[var(--cream-deep)]">
                    <div className="h-2.5 rounded-full bg-[var(--lala-blue)]" style={{ width: `${ratio}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="pillars-card">
          <div className="font-hand text-xl text-[var(--lala-blue)]">qué pilar les llamó más la atención</div>
          <h3 className="font-display text-2xl mb-3">Pilares más leídos</h3>
          {pillarData.length === 0 ? (
            <p className="text-sm text-[var(--ink-soft)]">Aún no hay datos suficientes. Una vez que los visitantes interactúen con los pilares, aparecerán aquí.</p>
          ) : (
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer width="100%" height={240} minWidth={200} minHeight={160}>
                <BarChart data={pillarData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#D9CFB9" />
                  <XAxis dataKey="pillar" stroke="#11142B" />
                  <YAxis stroke="#11142B" allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#FBF7EE", border: "1.5px solid #0033AD", borderRadius: 12 }} />
                  <Bar dataKey="views" fill="#0033AD" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Recent sessions */}
      <div className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="sessions-card">
        <div className="font-hand text-xl text-[var(--lala-blue)]">últimas 12 sesiones</div>
        <h3 className="font-display text-2xl mb-3">Quién entró y desde dónde</h3>
        {data.recent_sessions.length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)]">Sin sesiones aún. Comparte el link `/demo` o el QR del deck para empezar a recibir tráfico.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--line)] text-[var(--ink-soft)]">
                <tr>
                  <th className="py-2 px-3 text-left">Sesión</th>
                  <th className="py-2 px-3 text-left">Dispositivo</th>
                  <th className="py-2 px-3 text-right">Eventos</th>
                  <th className="py-2 px-3 text-right">Max scroll</th>
                  <th className="py-2 px-3 text-left">Empezó</th>
                  <th className="py-2 px-3 text-left">Origen</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_sessions.map((s, i) => (
                  <tr key={i} className="border-b border-[var(--line)]/60 hover:bg-white/50" data-testid={`session-row-${i}`}>
                    <td className="py-2 px-3 font-mono text-xs">{s.session_id}…</td>
                    <td className="py-2 px-3">
                      <span className="inline-flex items-center gap-1 text-xs">
                        {s.device === "mobile" ? <Smartphone size={12} /> : <Monitor size={12} />} {s.device}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right font-display text-base">{s.events}</td>
                    <td className="py-2 px-3 text-right">{s.max_scroll_pct}%</td>
                    <td className="py-2 px-3 text-xs text-[var(--ink-soft)]">{new Date(s.started_at).toLocaleString("es-MX")}</td>
                    <td className="py-2 px-3 text-xs text-[var(--ink-soft)] truncate max-w-[260px]">{s.referrer || "directo"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, sub, testid, highlight }) {
  return (
    <div className={`sketch-border-soft p-4 ${highlight ? "bg-[var(--lala-blue)] text-white" : "bg-[var(--cream-soft)]"}`} data-testid={testid}>
      <div className={`flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold ${highlight ? "opacity-80" : "text-[var(--lala-blue)]"}`}>
        {icon}<span>{label}</span>
      </div>
      <div className={`font-display text-3xl mt-1 ${highlight ? "" : "text-[var(--lala-blue)]"}`} aria-label={`${label}: ${value}`}>{value}</div>
      {sub && <div className={`text-xs mt-0.5 ${highlight ? "opacity-80" : "text-[var(--ink-soft)]"}`}>{sub}</div>}
    </div>
  );
}
